import React, { useContext } from 'react';

export enum EventModes {
  Before = 'BEFORE',
  PreRoll = 'PREROLL',
  Live = 'LIVE',
  After = 'AFTER',
}

type EventContext = {
  mode: EventModes;
  liveSession: string | null;
  allSessions: SessionNodes;
};

export type SessionNodes = {
  readonly nodes: readonly GatsbyTypes.SessionFragment[];
};

const defaultContext: EventContext = {
  mode: EventModes.Before,
  liveSession: null,
  allSessions: { nodes: [] },
};

const SESSION_START_BUFFER = 15;
const SESSION_START_BUFFER_MS = SESSION_START_BUFFER * 1000 * 60;

export const useEventMode = (sessions: GatsbyTypes.SessionsJsonConnection): EventContext => {
  const now: Date = new Date(Date.now());

  const firstEvent: Date = new Date(sessions?.nodes[0]?.date || Date.now());
  const lastEvent: Date = new Date(sessions?.nodes[sessions?.nodes.length - 1]?.date || Date.now());

  const liveEvent = sessions.nodes.reduce((liveSession: string | null, session: GatsbyTypes.SessionFragment) => {
    if (liveSession) return liveSession;

    const sessionStart: Date = new Date(session?.date || Date.now());
    const sessionEnd: Date = new Date(session?.date || Date.now());

    // default to 40 minutes if duration isn't set
    const duration = session.duration || 40;

    sessionEnd.setMinutes(sessionEnd.getMinutes() + duration);

    sessionStart.setTime(sessionStart.getTime() - SESSION_START_BUFFER_MS);

    return now > sessionStart && now < sessionEnd ? session.id : null;
  }, null);

  if (liveEvent) {
    const sessionDetails = sessions.nodes.find(({ id }) => id === liveEvent);
    const start = new Date(sessionDetails?.date || Date.now());
    const isPreRoll = now < start;

    return isPreRoll
      ? { mode: EventModes.PreRoll, liveSession: liveEvent, allSessions: sessions }
      : { mode: EventModes.Live, liveSession: liveEvent, allSessions: sessions };
  } else if (now > lastEvent) {
    return { ...defaultContext, mode: EventModes.After, allSessions: sessions };
  } else {
    return { ...defaultContext, allSessions: sessions };
  }
};

export const EventModeContext = React.createContext(defaultContext);
export const useEventModeContext = () => useContext(EventModeContext);
